/**
 * PIF local-form ↔ Vuex collection sync helpers.
 * Keep PersonalInformationForm.vue thin; behavior is covered here.
 *
 * @see apps/ejcrm-api/resources/assets/js/trip-dashboard/components/forms/PersonalInformationForm/PersonalInformationForm.vue
 */

/**
 * Escape text for safe interpolation into HTML tab headers.
 * @param {unknown} text
 * @returns {string}
 */
export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Derive the tab/label string the same way PHP getLabelAttribute does:
 * both first_name and last_name must be present, else fall back to stored label.
 *
 * @see apps/ejcrm-api/app/Models/PersonalInformationForm.php getLabelAttribute
 * @param {Record<string, unknown>|null|undefined} pif
 * @returns {string}
 */
export function pifTravelerDisplayName(pif) {
  if (!pif || typeof pif !== 'object') {
    return ''
  }
  const first = pif.first_name
  const last = pif.last_name
  if (first && last) {
    return `${first} ${last}`
  }
  return pif.label == null ? '' : String(pif.label)
}

/**
 * HTML for a traveler tab header (display name escaped).
 * @param {Record<string, unknown>|null|undefined} pif
 * @param {number} index zero-based traveler index
 * @returns {string}
 */
export function pifTravelerTabHeaderHtml(pif, index) {
  const displayName = pifTravelerDisplayName(pif)
  const escaped = displayName ? escapeHtml(displayName) : ''
  return `<span>Traveler #${index + 1}</span>${
    escaped ? `<span class="name">${escaped}</span>` : ''
  }`
}

/**
 * Ensure collection rows carry a display label when both names are present
 * (matches PHP accessor). Otherwise leave label unchanged.
 * @param {Record<string, unknown>} pif
 * @returns {Record<string, unknown>}
 */
export function withDerivedPifLabel(pif) {
  if (!pif || typeof pif !== 'object') {
    return pif
  }
  if (pif.first_name && pif.last_name) {
    return {...pif, label: `${pif.first_name} ${pif.last_name}`}
  }
  return {...pif}
}

/**
 * Replace-or-append one PIF in the collection by id (full row upsert).
 * Callers must pass a full PIF row — this replaces the previous row by id.
 * String/number ids are treated as equal so route params vs API ids do not duplicate.
 * @param {Array<Record<string, unknown>>} collection
 * @param {Record<string, unknown>|null|undefined} pif
 * @returns {Array<Record<string, unknown>>}
 */
export function upsertPifInCollection(collection, pif) {
  if (!pif || pif.id == null) {
    return collection || []
  }
  const idNum = Number(pif.id)
  if (Number.isNaN(idNum)) {
    return collection || []
  }
  const list = collection || []
  const nextRow = withDerivedPifLabel(pif)
  const index = list.findIndex(item => item && Number(item.id) === idNum)
  if (index === -1) {
    return list.concat([nextRow])
  }
  const next = list.slice()
  next[index] = nextRow
  return next
}

/**
 * Normalize PIFsV2 setOne payloads.
 * Supported:
 *   - `{ resource: pifRow }` (explicit wrapper)
 *   - plain `pifRow` with `id`
 * Anything else is ignored (no silent accept of malformed payloads).
 * @param {unknown} payload
 * @returns {Record<string, unknown>|null}
 */
export function resolvePifSetOneResource(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const asRecord = /** @type {Record<string, unknown>} */ (payload)
  if (Object.prototype.hasOwnProperty.call(asRecord, 'resource')) {
    const resource = asRecord.resource
    if (!resource || typeof resource !== 'object') {
      return null
    }
    const row = /** @type {Record<string, unknown>} */ (resource)
    return row.id != null ? row : null
  }
  return asRecord.id != null ? asRecord : null
}

/**
 * Hydrate local formData from a store PIF (loadFormData core).
 * @param {Record<string, unknown>} currentPIF
 * @returns {Record<string, unknown>}
 */
export function hydrateFormDataFromPif(currentPIF) {
  return {
    ...currentPIF,
    address_country_id: currentPIF.address_country_id ?? 251,
    citizenship_country_id: currentPIF.citizenship_country_id ?? 251
  }
}

/**
 * Before switching travelers: stash leaving local form into the collection,
 * then hydrate the incoming PIF from that updated collection (not a stale snapshot).
 *
 * @param {{
 *   collection: Array<Record<string, unknown>>,
 *   leavingFormData: Record<string, unknown>|null|undefined,
 *   leavingPifId: number|string|null|undefined,
 *   incomingPifId: number|string,
 * }} args
 * @returns {{ collection: Array<Record<string, unknown>>, formData: Record<string, unknown>|null }}
 */
export function switchActivePifPreservingLocalForm({
  collection,
  leavingFormData,
  leavingPifId,
  incomingPifId
}) {
  let nextCollection = collection || []
  const leavingId = leavingPifId != null ? Number(leavingPifId) : null
  const incomingId = Number(incomingPifId)

  if (
    leavingFormData &&
    leavingFormData.id != null &&
    leavingId != null &&
    !Number.isNaN(leavingId) &&
    !Number.isNaN(incomingId) &&
    Number(leavingFormData.id) === leavingId &&
    leavingId !== incomingId
  ) {
    nextCollection = upsertPifInCollection(nextCollection, leavingFormData)
  }

  const incoming =
    nextCollection.find(item => item && Number(item.id) === incomingId) || null
  if (!incoming) {
    return {collection: nextCollection, formData: null}
  }
  return {
    collection: nextCollection,
    formData: hydrateFormDataFromPif(incoming)
  }
}

/**
 * Whether an in-flight update response still belongs to the active traveler form.
 * Late responses for a previous PIF must not mutate the current form's UI state.
 *
 * @param {number|string|null|undefined} requestPifId
 * @param {number|string|null|undefined} activePifId
 * @returns {boolean}
 */
export function isResponseForActivePif(requestPifId, activePifId) {
  if (requestPifId == null || activePifId == null) {
    return false
  }
  const requestId = Number(requestPifId)
  const activeId = Number(activePifId)
  if (Number.isNaN(requestId) || Number.isNaN(activeId)) {
    return false
  }
  return requestId === activeId
}

/**
 * Client-side required-field names for the validation summary.
 * Gate on validationSurfaced so incomplete forms do not list every missing
 * field on first autosave / mount.
 *
 * @param {{
 *   errorsForm: Record<string, boolean>|null|undefined,
 *   validationSurfaced: boolean,
 * }} args
 * @param {(errorsForm: Record<string, boolean>|null|undefined) => string[]} fieldNamesFromErrorsForm
 * @returns {string[]}
 */
export function clientValidationNamesForSummary(
  {errorsForm, validationSurfaced},
  fieldNamesFromErrorsForm
) {
  if (!validationSurfaced) {
    return []
  }
  return fieldNamesFromErrorsForm(errorsForm)
}

/**
 * Field names shown in the PIF validation summary UI.
 * Empty until validationSurfaced (Submit); then merges server + client names.
 *
 * @param {{
 *   validationSurfaced: boolean,
 *   errorsForm: Record<string, boolean>|null|undefined,
 *   serverValidationErrors: Record<string, string[]>|null|undefined,
 * }} state
 * @param {{
 *   fieldNamesFromErrorsForm: (errorsForm: Record<string, boolean>|null|undefined) => string[],
 *   fieldNamesFromServerValidationErrors: (errors: Record<string, string[]>|null|undefined) => string[],
 *   mergeValidationSummaryFieldNames: (server: string[], client: string[]) => string[],
 * }} deps
 * @returns {string[]}
 */
export function pifValidationSummaryFieldNames(state, deps) {
  if (!state.validationSurfaced) {
    return []
  }
  const server = deps.fieldNamesFromServerValidationErrors(state.serverValidationErrors)
  const client = clientValidationNamesForSummary(
    {errorsForm: state.errorsForm, validationSurfaced: true},
    deps.fieldNamesFromErrorsForm
  )
  if (server.length === 0 && client.length === 0) {
    return []
  }
  return deps.mergeValidationSummaryFieldNames(server, client)
}

/**
 * Whether executeUpdate / autosave should populate errorsForm into the summary.
 * Explicit submit and already-surfaced flows may refresh; quiet autosave must not.
 *
 * @param {{ explicit?: boolean, validationSurfaced?: boolean }} args
 * @returns {boolean}
 */
export function shouldRefreshClientValidationSummary({
  explicit = false,
  validationSurfaced = false
} = {}) {
  return Boolean(explicit || validationSurfaced)
}
